import {
  Box,
  Chip,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { formatDealAmount } from 'helper/short-currency';
import { useNavigate } from 'react-router-dom';
import RoutePaths from 'routes/RoutePaths';
import zeplinColor from 'theme/Color';

export type DealRow = {
  id: string;
  name: string;
  value: number;
  status: string;
  days: number;
};

const getDealStatusColor = (status: string) => {
  switch (status.trim().toLowerCase()) {
    case 'exploring':
    case 'identified':
      return zeplinColor.Brand1;
    case 'qualified':
    case 'pitching':
      return zeplinColor.Blue;
    case 'negotiating':
    case 'contracting':
      return zeplinColor.AlertInfo;
    case 'active':
      return zeplinColor.Success;

    default:
      return zeplinColor.Success;
  }
};

const ActiveDealsTable = ({ deals, loading }: { deals: DealRow[]; loading?: boolean }) => {
  const navigate = useNavigate();
  const handleDealClick = (deal: DealRow) => navigate(`${RoutePaths.Deals}/${deal.id}`);
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
      }}
    >
      <Paper sx={{ width: '100%', border: 'none', boxShadow: 'none' }}>
        <TableContainer
          sx={{
            overflow: 'auto',
            border: `1px solid ${zeplinColor.Neutral5}`,
            borderRadius: '14px',
            pt: '16px',
          }}
        >
          <Typography
            variant='responsiveHeading4'
            color={zeplinColor.BasicBlack}
            sx={{ p: '16px' }}
          >
            Active Deals
          </Typography>
          <Table stickyHeader aria-label='active deals table' sx={{ mt: '16px' }}>
            <TableHead>
              <TableRow
                sx={{
                  '& th': {
                    backgroundColor: zeplinColor.Neutral3,
                    color: zeplinColor.BasicBlack,
                    fontWeight: 500,
                  },
                  '& th:first-of-type': { borderRadius: '14px 0 0 14px' },
                  '& th:last-of-type': { borderRadius: '0 14px 14px 0' },
                }}
              >
                {['Deal', 'Value', 'Status', 'Days'].map((col) => (
                  <TableCell key={col}>
                    <Typography variant='responsiveBody3' sx={{ color: zeplinColor.BasicBlack }}>
                      {col}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 4 }).map((__, j) => (
                      <TableCell key={j}>
                        <Skeleton variant='text' width='80%' />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : deals.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} sx={{ border: 'none', p: '48px 16px' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px',
                        justifyContent: 'center',
                        alignItems: 'center',
                        textAlign: 'center',
                      }}
                    >
                      <img src='/assets/no-chart.svg' width='36px' height='36px' alt='no-data' />
                      <Typography variant='responsiveHeading3'>No Active Deals</Typography>
                      <Typography variant='responsiveBody3' color={zeplinColor.Neutral8}>
                        There are no active deals to display right now.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                deals.map((deal) => (
                  <TableRow
                    key={deal.id}
                    hover
                    sx={{
                      cursor: 'pointer',
                      '& td': { borderBottom: `1px solid ${zeplinColor.Neutral5}` },
                    }}
                    onClick={() => handleDealClick(deal)}
                  >
                    <TableCell>
                      <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
                        {deal.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
                        {formatDealAmount(deal.value)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={deal.status}
                        sx={{
                          backgroundColor: getDealStatusColor(deal.status),
                          color: zeplinColor.Neutral1,
                          borderRadius: '50px',
                          height: '24px',
                          '& .MuiChip-label': { px: '10px' },
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
                        {deal.days > 0 ? `${deal.days}d` : '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default ActiveDealsTable;
